import React, {useEffect, useMemo, useState} from 'react'
import {Alert, Box, Button} from '@mui/material'
import {SxProps, Theme} from '@mui/material/styles'
import styles from './styles'

export type AlertMessageData = {
  message: string | undefined
  details: string[] | undefined
}

type AlertMessageSeverity = 'error' | 'warning' | 'success'

type AlertMessageProps = {
  error?: AlertMessageData | string
  severity?: AlertMessageSeverity
  sx?: SxProps<Theme>
  maxPreviewCount?: number
}

const AlertMessage: React.FC<AlertMessageProps> = ({
  error,
  severity = 'error',
  sx,
  maxPreviewCount = 3,
}) => {
  const [showAllErrors, setShowAllErrors] = useState(false)

  useEffect(() => {
    setShowAllErrors(false)
  }, [error])

  const errorMessage = useMemo(() => {
    if (typeof error === 'string') {
      return error
    }
    return error?.message
  }, [error])

  const errorDetails = useMemo(() => {
    if (typeof error === 'string') {
      return []
    }

    if (!error?.details) {
      return [] as string[]
    }
    return error.details.reduce<string[]>((accumulator, detail) => {
      if (detail?.trim()) {
        accumulator.push(detail.trim())
      }
      return accumulator
    }, [])
  }, [error])

  const displayedErrors = showAllErrors ? errorDetails : errorDetails.slice(0, maxPreviewCount)
  const shouldShowToggle = errorDetails.length > maxPreviewCount

  if (!error) {
    return null
  }

  return (
    <Alert severity={severity} sx={[styles.alert, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
      <Box component="div" sx={styles.message}>
        {errorMessage}
      </Box>
      {errorDetails.length > 0 && (
        <>
          <Box
            component="div"
            sx={{
              ...styles.details,
              ...(showAllErrors ? styles.detailsExpanded : {}),
            }}>
            <ul>
              {displayedErrors.map((detail, index) => (
                <li key={`${index}-${detail}`}>{detail}</li>
              ))}
            </ul>
          </Box>
          {shouldShowToggle && (
            <Button
              size="small"
              variant="text"
              onClick={() => setShowAllErrors(current => !current)}
              sx={styles.toggleButton}>
              {showAllErrors
                ? 'Show fewer messages'
                : `Show all ${errorDetails.length} messages`}
            </Button>
          )}
        </>
      )}
    </Alert>
  )
}

export type {AlertMessageSeverity}
export default AlertMessage
